import { useState, useEffect } from "react";
import { Order } from "../../ws/types";
import { toast } from "../ui/Toast";
import "../../styles/modals/AmountSelectionModal.scss";

interface Props {
    order: Order;
    message?: string;
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (amount: number) => void;
}

export default function AmountSelectionModal({
    order,
    message,
    isOpen,
    onClose,
    onSubmit
}: Props) {
    const [partialAmount, setPartialAmount] = useState<string>(order.amount.toString());

    // Reset amount when order changes
    useEffect(() => {
        if (isOpen) {
            setPartialAmount(order.amount.toString());
        }
    }, [isOpen, order.amount]);

    const handleSubmit = () => {
        const amount = parseFloat(partialAmount);
        if (isNaN(amount) || amount <= 0 || amount > order.amount) {
            toast.error("Неверная сумма");
            return;
        }
        onSubmit(amount);
    };

    if (!isOpen) return null;

    const fillPercentage = order.filled_amount && order.original_amount
        ? (order.filled_amount / order.original_amount) * 100
        : 0;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content amount-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Укажите количество</h3>
                    <button className="close-btn" onClick={onClose}>
                        ✕
                    </button>
                </div>
                <div className="modal-body">
                    <p className="modal-description">
                        Вы можете взять весь ордер или указать частичную сумму
                    </p>
                    
                    <div className="amount-info">
                        <div className="info-row">
                            <span>Доступно:</span>
                            <strong>{order.amount} {order.jetton_symbol}</strong>
                        </div>
                        
                        {/* Show progress if order is partially filled */}
                        {order.filled_amount && order.original_amount && order.filled_amount > 0 && (
                            <div className="progress-section">
                                <span className="progress-label">
                                    Прогресс выполнения: {order.filled_amount.toFixed(2)} / {order.original_amount.toFixed(2)}
                                </span>
                                <div className="progress-bar">
                                    <div 
                                        className="progress-fill" 
                                        style={{ width: `${fillPercentage}%` }}
                                    />
                                </div>
                                <span className="progress-percent">
                                    {fillPercentage.toFixed(0)}%
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="form-group">
                        <label htmlFor="partial-amount-input">Количество *</label>
                        <input
                            id="partial-amount-input"
                            type="number"
                            value={partialAmount}
                            onChange={(e) => setPartialAmount(e.target.value)}
                            max={order.amount}
                            min="0"
                            step="any"
                            placeholder={`Максимум: ${order.amount}`}
                        />
                        <small className="input-hint">
                            Фиатная сумма: ~{(parseFloat(partialAmount || "0") * order.price).toFixed(2)} {order.fiat_currency}
                        </small>
                    </div>

                    <div className="quick-amounts">
                        <button 
                            className="btn-quick"
                            onClick={() => setPartialAmount((order.amount * 0.25).toString())}
                        >
                            25%
                        </button>
                        <button 
                            className="btn-quick"
                            onClick={() => setPartialAmount((order.amount * 0.5).toString())}
                        >
                            50%
                        </button>
                        <button 
                            className="btn-quick"
                            onClick={() => setPartialAmount((order.amount * 0.75).toString())}
                        >
                            75%
                        </button>
                        <button 
                            className="btn-quick"
                            onClick={() => setPartialAmount(order.amount.toString())}
                        >
                            100%
                        </button>
                    </div>
                </div>
                <div className="modal-footer">
                    <button
                        className="btn btn-secondary"
                        onClick={onClose}
                    >
                        Отмена
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={handleSubmit}
                        disabled={!partialAmount || parseFloat(partialAmount) <= 0 || parseFloat(partialAmount) > order.amount}
                    >
                        Взять {partialAmount || '0'} {order.jetton_symbol}
                    </button>
                </div>
            </div>
        </div>
    );
}
